// External Database Sources — the browser's way in.
//
// Two audiences:
//   1. The Settings page (admin): externalSourcesManage … externalSourceRefresh
//      — merged into @app/services/api with the other module APIs.
//   2. Everything else in the browser — a client custom page, a grid column:
//
//        import { externalSources, useExternalQuery } from '@modules/database/external-sources/frontend/api';
//
//        const list = await externalSources.list();          // the sources this person may read
//        const { rows } = await externalSources.query('machine-rates', {
//          table: 'Machine Rates', where: { 'Issue Type': 'Asset' }, orderBy: 'Key', limit: 100,
//        });
//        const hit = await externalSources.lookup('machine-rates', { table: 'Machine Rates', match: 'Key', values: ['MC-606'] });
//
//        // in a component — loads, re-loads when the query changes, says loading/error
//        const { rows, loading, error, reload } = useExternalQuery('machine-rates', { table: 'Machine Rates', limit: 50 });
//
// A custom page reaches this through its own stride-host.js re-export (the
// customs convention). The connection details of a source never come to the
// browser — not on any of these calls.

import { useCallback, useEffect, useRef, useState } from 'react';
import { client } from '@platform/http/api-client';

const B = '/api/external-sources';
const enc = encodeURIComponent;
const withConsumer = (consumer) => (consumer ? { headers: { 'X-Stride-Consumer': String(consumer) } } : undefined);

const externalSourcesApi = {
  // ---- Settings → Automation → External Database Sources (site admins)
  async externalSourcesManage() { return (await client.get(`${B}/manage`)).data; },
  async externalSourceTest(body) { return (await client.post(`${B}/manage/test`, body)).data; },
  async externalSourceCreate(body) { return (await client.post(`${B}/manage`, body)).data; },
  async externalSourceGet(id) { return (await client.get(`${B}/manage/${enc(id)}`)).data; },
  async externalSourceUpdate(id, body) { return (await client.patch(`${B}/manage/${enc(id)}`, body)).data; },
  async externalSourceDelete(id) { return (await client.delete(`${B}/manage/${enc(id)}`)).data; },
  async externalSourceRefresh(id) { return (await client.post(`${B}/manage/${enc(id)}/refresh`)).data; },
  async externalSourcePreview(id, params = {}) { return (await client.get(`${B}/manage/${enc(id)}/preview`, { params })).data; },

  // ---- reading (any signed-in user the source lets read)
  async externalSourcesList() { return (await client.get(B)).data.sources || []; },
  async externalSourceTables(key) { return (await client.get(`${B}/${enc(key)}`)).data; },
  async externalSourceQuery(key, q = {}, { consumer } = {}) { return (await client.post(`${B}/${enc(key)}/query`, q, withConsumer(consumer))).data; },
  async externalSourceRows(key, params = {}, { consumer } = {}) { return (await client.get(`${B}/${enc(key)}/rows`, { params, ...(withConsumer(consumer) || {}) })).data; },
  async externalSourceLookup(key, body = {}, { consumer } = {}) { return (await client.post(`${B}/${enc(key)}/lookup`, body, withConsumer(consumer))).data; },
  async externalSourceDistinct(key, params = {}, { consumer } = {}) { return (await client.get(`${B}/${enc(key)}/distinct`, { params, ...(withConsumer(consumer) || {}) })).data; },
};

export default externalSourcesApi;

// The plain words for custom pages and other features.
export const externalSources = {
  list: () => externalSourcesApi.externalSourcesList(),
  tables: (key) => externalSourcesApi.externalSourceTables(key),
  query: (key, q, opts) => externalSourcesApi.externalSourceQuery(key, q, opts),
  rows: async (key, table, q = {}, opts) => (await externalSourcesApi.externalSourceQuery(key, { ...q, table }, opts)).rows,
  lookup: (key, body, opts) => externalSourcesApi.externalSourceLookup(key, body, opts),
  value: async (key, { table, match, value, column }, opts) => {
    const r = await externalSourcesApi.externalSourceLookup(key, { table, match, values: [value], returns: [column] }, opts);
    const row = r.values[value === null || value === undefined ? '' : String(value)];
    return row ? row[Object.keys(row)[0]] ?? null : null;
  },
  distinct: (key, params, opts) => externalSourcesApi.externalSourceDistinct(key, params, opts),
};

// A query as React state: { rows, columns, total, source, loading, error, reload }.
// `query` is compared by value, so an inline object does not loop.
export function useExternalQuery(key, query = {}, { enabled = true, consumer } = {}) {
  const [state, setState] = useState({ rows: [], columns: [], total: 0, source: null, loading: !!(enabled && key), error: null });
  const qs = JSON.stringify(query || {});
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!enabled || !key) { setState((s) => ({ ...s, loading: false })); return; }
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await externalSourcesApi.externalSourceQuery(key, JSON.parse(qs), { consumer });
      if (mine === seq.current) setState({ rows: r.rows || [], columns: r.columns || [], total: r.total || 0, source: r.source || null, loading: false, error: null });
    } catch (e) {
      if (mine === seq.current) setState((s) => ({ ...s, loading: false, error: (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Could not read the source' }));
    }
  }, [key, qs, enabled, consumer]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}
